import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, FolderPlus, Inbox, Loader2, Search, Tag, X } from 'lucide-react';
import type { TodoProject, TodoProjectColorKey } from '../../types';
import {
  TODO_PROJECT_COLOR_KEYS,
  TODO_PROJECT_COLOR_TOKENS,
  sortProjectsForPivot,
} from '../../utils/todoSwimLanes';

interface ProjectAssignButtonProps {
  projects: TodoProject[];
  /** Currently assigned project (resolved from id by the caller). `null` when card is in Inbox. */
  current: TodoProject | null;
  onAssign: (projectId: string | null) => Promise<void> | void;
  onCreate: (name: string, colorKey?: TodoProjectColorKey) => Promise<string | null> | string | null;
  /** Optional dense rendering for tight cells. */
  compact?: boolean;
}

/**
 * Flank-branded popover that lets the user assign / unassign / create a `TodoProject` for one card.
 * Strict: no native dialogs (workspace `no-browser-dialogs` rule).
 */
export function ProjectAssignButton({
  projects,
  current,
  onAssign,
  onCreate,
  compact,
}: ProjectAssignButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const popoverWidth = 280;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - popoverWidth - 8
    );
    setPosition({ top: rect.bottom + 6, left });
  }, [open]);

  const colorTokens = current?.colorKey
    ? TODO_PROJECT_COLOR_TOKENS[current.colorKey]
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          compact
            ? 'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors focus:outline-none flank-focus-ring'
            : 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors focus:outline-none flank-focus-ring'
        }
        style={
          current
            ? {
                background: colorTokens?.tint ?? 'var(--color-surface)',
                color: colorTokens?.fg ?? 'var(--color-text-primary)',
                border: '1px solid transparent',
              }
            : {
                background: 'transparent',
                color: 'var(--color-text-muted)',
                border: '1px dashed var(--color-border)',
              }
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        title={current ? `Project: ${current.name}` : 'Assign to a project'}
      >
        {current ? <Tag size={compact ? 11 : 12} /> : <FolderPlus size={compact ? 11 : 12} />}
        <span className="truncate max-w-[160px]">
          {current ? current.name : 'Add project'}
        </span>
      </button>
      {open ? (
        <ProjectPickerPopover
          anchor={position}
          projects={projects}
          current={current}
          onClose={() => setOpen(false)}
          onAssign={async (projectId) => {
            await onAssign(projectId);
            setOpen(false);
          }}
          onCreate={async (name, colorKey) => {
            const id = await onCreate(name, colorKey);
            if (id) {
              await onAssign(id);
              setOpen(false);
            }
          }}
        />
      ) : null}
    </>
  );
}

interface PopoverProps {
  anchor: { top: number; left: number } | null;
  projects: TodoProject[];
  current: TodoProject | null;
  onClose: () => void;
  onAssign: (projectId: string | null) => Promise<void>;
  onCreate: (name: string, colorKey?: TodoProjectColorKey) => Promise<void>;
}

export function ProjectPickerPopover({
  anchor,
  projects,
  current,
  onClose,
  onAssign,
  onCreate,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<TodoProjectColorKey>('teal');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const sorted = useMemo(
    () => sortProjectsForPivot(projects, { includeArchived: false }),
    [projects]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => p.name.toLowerCase().includes(q));
  }, [sorted, search]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Assign project"
      className="fixed z-50 w-[280px] flank-card-elevated"
      style={{
        top: anchor.top,
        left: anchor.left,
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
        padding: 6,
      }}
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <Search size={14} className="text-[var(--color-text-muted)]" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find or create a project"
          className="flex-1 text-sm bg-transparent text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none"
        />
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface)]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>

      <div className="max-h-[260px] overflow-y-auto px-1 py-1 flex flex-col gap-0.5">
        <button
          type="button"
          onClick={async () => {
            setBusy(true);
            await onAssign(null);
            setBusy(false);
          }}
          className="flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
        >
          <Inbox size={14} />
          <span className="flex-1">Inbox (no project)</span>
          {current === null ? <Check size={14} className="text-[var(--color-success)]" /> : null}
        </button>

        {filtered.map((p) => {
          const tokens = p.colorKey ? TODO_PROJECT_COLOR_TOKENS[p.colorKey] : null;
          const isCurrent = current?.id === p.id;
          return (
            <button
              key={p.id}
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await onAssign(p.id);
                setBusy(false);
              }}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface)]"
            >
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: tokens?.fg ?? 'var(--color-text-muted)',
                  flexShrink: 0,
                }}
              />
              <span className="flex-1 truncate">{p.name}</span>
              {isCurrent ? <Check size={14} className="text-[var(--color-success)]" /> : null}
            </button>
          );
        })}

        {filtered.length === 0 && search.trim() ? (
          <p className="px-2 py-2 text-xs text-[var(--color-text-muted)]">
            No matches. Create a project below.
          </p>
        ) : null}
      </div>

      <div className="border-t border-[var(--color-border)] mt-1 pt-2 px-2 pb-2">
        {creating ? (
          <div className="flex flex-col gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Project name"
              maxLength={120}
              className="w-full px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-[var(--color-border-focus)]"
            />
            <div className="flex flex-wrap items-center gap-1.5">
              {TODO_PROJECT_COLOR_KEYS.map((key) => {
                const tokens = TODO_PROJECT_COLOR_TOKENS[key];
                const selected = newColor === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewColor(key)}
                    aria-label={`Use ${key} accent`}
                    className="w-5 h-5 rounded-full transition-transform"
                    style={{
                      background: tokens.fg,
                      transform: selected ? 'scale(1.15)' : 'scale(1)',
                      outline: selected ? '2px solid var(--color-border-focus)' : '2px solid transparent',
                      outlineOffset: 1,
                    }}
                  />
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewName('');
                }}
                className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] px-2 py-1 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || !newName.trim()}
                onClick={async () => {
                  if (!newName.trim()) return;
                  setBusy(true);
                  try {
                    await onCreate(newName, newColor);
                    setCreating(false);
                    setNewName('');
                  } finally {
                    setBusy(false);
                  }
                }}
                className="flank-btn flank-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : 'Create'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text-primary)]"
          >
            <FolderPlus size={14} />
            New project
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
